import React, { useEffect, useMemo, useState } from "react";
import ToolShell from "@/components/ToolShell";
import { track } from "@/lib/analytics";
import { Search } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function GlossaryPage() {
  const [terms, setTerms] = useState([]);
  const [q, setQ] = useState("");

  useEffect(() => {
    track.tool_opened("glossary");
    fetch(`${API}/glossary`).then(r => r.json()).then(d => setTerms(d.list || []));
  }, []);

  useEffect(() => {
    if (q) track.glossary_searched();
  }, [q]);

  const filtered = useMemo(() => {
    const query = q.toLowerCase().trim();
    if (!query) return terms;
    return terms.filter(t =>
      t.term.toLowerCase().includes(query) ||
      t.short.toLowerCase().includes(query) ||
      t.full.toLowerCase().includes(query)
    );
  }, [terms, q]);

  const grouped = useMemo(() => {
    const map = new Map();
    for (const t of filtered) {
      const letter = t.term[0].toUpperCase();
      if (!map.has(letter)) map.set(letter, []);
      map.get(letter).push(t);
    }
    return [...map.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [filtered]);

  return (
    <ToolShell wide tool="Reference · Glossary" title="FAFSA terms, in plain English." subtitle={`${terms.length} terms, sorted A-Z. Search, skim, link to the definition.`} hidePrivacyBadge>
      <div className="card mb-6" data-testid="glossary-search-card">
        <div className="relative">
          <Search className="w-4 h-4 text-ink-dim absolute left-4 top-1/2 -translate-y-1/2" />
          <input className="input pl-10" placeholder="Search terms… (e.g. SAI, Pell, verification)" value={q} onChange={e => setQ(e.target.value)} data-testid="glossary-search" />
        </div>
      </div>

      <div className="space-y-8">
        {grouped.map(([letter, items]) => (
          <section key={letter} data-testid={`glossary-letter-${letter}`}>
            <div className="text-xs uppercase tracking-[0.22em] text-ink-dim mb-3">{letter}</div>
            <div className="grid md:grid-cols-2 gap-4">
              {items.map(t => (
                <div key={t.term} className="card-tight card" data-testid={`glossary-term-${t.term.replace(/\s+/g, "-")}`}>
                  <div className="flex items-baseline justify-between gap-3 mb-2">
                    <h3 className="font-heading text-xl text-ink">{t.term}</h3>
                    <span className="text-xs text-ink-dim uppercase tracking-widest">{t.short}</span>
                  </div>
                  <p className="text-sm text-ink-muted leading-relaxed">{t.full}</p>
                </div>
              ))}
            </div>
          </section>
        ))}
        {grouped.length === 0 && (
          <div className="card text-ink-muted" data-testid="glossary-empty">No terms match "{q}". Try a broader search.</div>
        )}
      </div>
    </ToolShell>
  );
}
